import React from "react";
import { css, styled } from "styled-components";
import BarGraph from "../BarGraph";
import DoughnutChart2 from "../../../../component/mobile/DoughnutChart2";
import { FarmComparison } from "../../machine";
import { formatPriceNumber, formatWonToEokMan } from "../../function";

interface Props {
  data: FarmComparison[];
}

function Cost({ data }: Props) {
  const myFarm = data[1];

  const x =
    myFarm.empLaborCost +
    myFarm.ownLaborCost +
    myFarm.materialCost +
    myFarm.waterHeatingCost +
    myFarm.farmEquipmentRentalCost +
    myFarm.sga +
    myFarm.tractorMaintenanceCost;

  const costArr = [
    {
      color: "#FF507B",
      text: "인건비",
      rate: ((myFarm.empLaborCost + myFarm.ownLaborCost) * 100) / x,
      value: myFarm.empLaborCost + myFarm.ownLaborCost,
    },
    {
      color: "#F5A002",
      text: "재료비",
      rate: (myFarm.materialCost * 100) / x,
      value: myFarm.materialCost,
    },
    {
      color: "#FFC313",
      text: "수도 광열비",
      rate: (myFarm.waterHeatingCost * 100) / x,
      value: myFarm.waterHeatingCost,
    },
    {
      color: "#209C6C",
      text: "농구비",
      rate: (myFarm.farmEquipmentRentalCost * 100) / x,
      value: myFarm.farmEquipmentRentalCost,
    },
    {
      color: "#1F75F7",
      text: "기타 판매관리비",
      rate: (myFarm.sga * 100) / x,
      value: myFarm.sga,
    },
    {
      color: "#C6D0DD",
      text: "트랙터부속기 유지비",
      rate: (myFarm.tractorMaintenanceCost * 100) / x,
      value: myFarm.tractorMaintenanceCost,
    },
  ];

  const biggestMyCost = costArr.reduce((a, b) => (a.value > b.value ? a : b));

  return (
    <Container>
      <GraphSection>
        <RowContent>
          <HeaderLabel>경작기간 총 비용</HeaderLabel>
          <HeaderValue>{formatWonToEokMan(x)}</HeaderValue>
        </RowContent>
        <GraphContainer>
          <div className="w-50per center-y">
            <DoughnutChart2 list={costArr} />
          </div>
          <div className="flex-column gap-8" style={{ width: "144px" }}>
            {costArr.map((el) => (
              <div className="flex-row space-between">
                <div className="flex-row center-y gap-8">
                  <div
                    style={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "100%",
                      background: el.color,
                    }}
                  ></div>
                  <div className="font-medium f-size-14 line-h-20 color-text-tertiary">
                    {el.text}
                  </div>
                </div>
                <div className="font-medium f-size-14 line-h-20 color-text-primary">
                  {Math.round(el.rate * 10) / 10}%
                </div>
              </div>
            ))}
          </div>
        </GraphContainer>
      </GraphSection>
      <Section>
        <RowContent>
          <ContentLabel>인건비</ContentLabel>
          <ContentValue>
            {formatWonToEokMan(myFarm.empLaborCost + myFarm.ownLaborCost)}
          </ContentValue>
        </RowContent>
        <RowContent>
          <ContentLabel>재료비</ContentLabel>
          <ContentValue>{formatWonToEokMan(myFarm.materialCost)}</ContentValue>
        </RowContent>
        <RowContent>
          <ContentLabel>수도광열비</ContentLabel>
          <ContentValue>
            {formatWonToEokMan(myFarm.waterHeatingCost)}
          </ContentValue>
        </RowContent>
        <RowContent>
          <ContentLabel>농구비</ContentLabel>
          <ContentValue>
            {formatWonToEokMan(myFarm.farmEquipmentRentalCost)}
          </ContentValue>
        </RowContent>
        <RowContent>
          <ContentLabel>기타 판매관리비</ContentLabel>
          <ContentValue>{formatWonToEokMan(myFarm.sga)}</ContentValue>
        </RowContent>
        <RowContent>
          <ContentLabel>트랙터부속기 유지비</ContentLabel>
          <ContentValue>{formatWonToEokMan(myFarm.tractorMaintenanceCost)}</ContentValue>
        </RowContent>
      </Section>

      <Bottom>
        <BottomContent>
          {localStorage.getItem("username")}님의 가장 큰 지출 항목은{" "}
          <span>{biggestMyCost.text}</span>
          이며, 전체 비용의{" "}
          <span>{Math.round(biggestMyCost.rate * 10) / 10}%</span>
          입니다.
        </BottomContent>
      </Bottom>
    </Container>
  );
}

export default Cost;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
`;

const GraphSection = styled.div`
  display: flex;
  padding: 8px 0 16px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
  align-self: stretch;
`;

const RowContent = styled.div`
  display: flex;
  padding: 8px 0;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
`;

const HeaderLabel = styled.div`
  color: var(--Alias-Color-Text-Dark-Text-Secondary, rgba(23, 30, 38, 0.78));
  font-feature-settings:
    "liga" off,
    "clig" off;

  /* Mo/Body/L-16-Medium */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 24px */
`;

const HeaderValue = styled.div`
  color: var(--Alias-Color-Text-Dark-Text-Secondary, rgba(23, 30, 38, 0.78));
  font-feature-settings:
    "liga" off,
    "clig" off;

  /* Mo/Body/L-16-Bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 150%; /* 24px */
`;

const Section = styled.div`
  display: flex;
  width: 100%;
  padding-bottom: 24px;
  flex-direction: column;
  align-items: flex-start;
`;

const GraphContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;

  padding: 16px 0;
`;

const ContentLabel = styled.div`
  color: var(--Alias-Color-Text-Dark-Text-Tertiary, rgba(23, 30, 38, 0.56));
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 24px */
`;

const ContentValue = styled.div`
  color: var(--Alias-Color-Text-Dark-Text-Secondary, rgba(23, 30, 38, 0.78));
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 24px */
`;

const ContentItem = styled.div`
  display: flex;
  padding: 8px 8px 16px 8px;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  align-self: stretch;
`;

const ItemHeader = styled.div`
  display: flex;
  padding: 4px 0;
  align-items: center;
  gap: 2px;
  align-self: stretch;

  & > .title {
    flex: 1 0 0;
    color: var(--Alias-Color-Text-Dark-Text-Secondary, rgba(23, 30, 38, 0.78));
    font-family: Pretendard;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 150%; /* 24px */
  }
`;

const ItemLegends = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
`;

const ItemLegend = styled.div<{ $isRequired?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;

  color: rgba(23, 30, 38, 0.78);
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px; /* 142.857% */

  & > .dot {
    width: 8px;
    height: 8px;
    border-radius: 9999px;
    background-color: #1e9b6b;
  }

  ${({ $isRequired }) =>
    $isRequired &&
    css`
      color: rgba(23, 30, 38, 0.56);

      & > .dot {
        background-color: #8de2c2;
      }
    `}
`;

const Bottom = styled.div`
  display: flex;
  width: 100%;
  padding: 8px 0 24px 0;
  flex-direction: column;
  align-items: center;
`;

const BottomContent = styled.div`
  width: 100%;
  padding: 16px;
  border-radius: 8px;
  background: var(--Alias-Color-Text-White-Gray-LightGray-100, #f7f8f9);

  color: var(--Alias-Color-Text-Dark-Text-Secondary, rgba(23, 30, 38, 0.78));
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 24px */

  & > span {
    color: var(--Alias-Color-Button-cta-light-background-Primary, #1e9b6b);
  }
`;
